import { createFileRoute, notFound } from "@tanstack/react-router";

import LoadingScreen from "@/core/ui/layout/LoadingScreen";
import { resolveAuditActive } from "@/features/project/components/pages/project-page-context-hooks";
import {
  getClassificationTemplate,
  getLatestClassificationTemplateVersion,
  queryClassificationTemplateData,
} from "@/openapi";
import { getProjectHierarchyOptions } from "@/openapi/@tanstack/react-query.gen";

export const Route = createFileRoute(
  "/_protected/auditor/clients/$id/classifications/$cls/",
)({
  pendingComponent: () => <LoadingScreen isActive noDim />,
  loaderDeps: ({ search }) => ({
    entityProjectId: (search as { entityProjectId?: string }).entityProjectId,
  }),
  loader: async ({ context, params, deps }) => {
    if (!context.user.organization) {
      throw notFound();
    }
    // resolve the active dossier: on canonical urls params.id is the client root
    const hierarchy = await context.queryClient.query({
      ...getProjectHierarchyOptions({
        path: {
          org_id: context.user.organization.id,
          project_id: params.id,
        },
      }),
      staleTime: Infinity,
    });
    const activeProjectId = resolveAuditActive(
      hierarchy.root,
      hierarchy.children,
      deps.entityProjectId,
    ).id;
    if (!context.user.organization?.id) throw new Error("Not authorized");

    const [template, templateVersion, cls] = await Promise.all([
      getClassificationTemplate({
        path: {
          template_id: params.cls,
          org_id: context.user.organization.id,
        },
      }),
      getLatestClassificationTemplateVersion({
        path: {
          template_id: params.cls,
          org_id: context.user.organization.id,
        },
      }),
      queryClassificationTemplateData({
        path: {
          template_id: params.cls,
          project_id: activeProjectId,
          org_id: context.user.organization.id,
        },
      }),
    ]);

    if (cls.error && "code" in cls.error) {
      return { error: cls.error.code };
    }

    if (cls.data && template.data && templateVersion.data) {
      return {
        data: cls.data,
        template: template.data,
        templateVersion: templateVersion.data,
      };
    }

    throw notFound();
  },
});
